import { getWinningRosterIds, Match } from "model/Match/Match";
import { getMatchSetWinnerIds } from "model/Match/Set/MatchSet";
import { getDefinedRosterIds } from "model/Roster/Roster";

export type SmsSeasonRecord = {
  rosterId: string;
  wins: number;
  loses: number;
  setsWon: number;
  setsLost: number;
  roundsWon: number;
  roundsLost: number;
  position: number;
};

export type SmsSeasonRecordWithDisplay = {
  record: SmsSeasonRecord;
  orgId: string | undefined;
  displayName: string | undefined;
};

const createSeasonRecord = (rosterId: string): SmsSeasonRecord => {
  return {
    rosterId,
    wins: 0,
    loses: 0,
    setsWon: 0,
    setsLost: 0,
    roundsWon: 0,
    roundsLost: 0,
    position: 0,
  };
};

export const calculateSmsSeasonRecords = (
  rosterIds: string[],
  matches: Match[]
) => {
  const records = new Map<string, SmsSeasonRecord>();
  rosterIds.forEach((id) => {
    records.set(id, createSeasonRecord(id));
  });

  const completeMatches = matches.filter((m) => m.isComplete);
  for (let i = 0; i < completeMatches.length; i++) {
    const match = completeMatches[i];
    const winnerIds = getWinningRosterIds(match);
    if (!winnerIds) {
      continue;
    }

    const rosterIds = getDefinedRosterIds(match.teams.rosters);
    for (let t = 0; t < rosterIds.length; t++) {
      const rosterId = rosterIds[t];
      const rosterRecord = records.get(rosterId);
      if (!rosterRecord) {
        continue;
      }

      if (winnerIds.includes(rosterId)) {
        rosterRecord.wins++;
      } else {
        rosterRecord.loses++;
      }
    }

    for (let s = 0; s < match.sets.length; s++) {
      const set = match.sets[s];
      if (set.type !== "Matchmaking") {
        continue;
      }

      const setWinnerIds = getMatchSetWinnerIds(set);
      if (!setWinnerIds) {
        continue;
      }

      for (let r = 0; r < rosterIds.length; r++) {
        const rosterId = rosterIds[r];
        const rosterRecord = records.get(rosterId);
        if (!rosterRecord) {
          continue;
        }

        if (setWinnerIds.includes(rosterId)) {
          rosterRecord.setsWon++;
        } else {
          rosterRecord.setsLost++;
        }
      }

      for (let t = 0; t < set.tracks.length; t++) {
        const track = set.tracks[t];
        const scoreTotal =
          Object.values(track.scores).reduce(
            (a, b) => (a ?? 0) + (b ?? 0),
            0
          ) ?? 0;

        for (let r = 0; r < rosterIds.length; r++) {
          const rosterId = rosterIds[r];
          const rosterRecord = records.get(rosterId);
          if (!rosterRecord) {
            continue;
          }

          const rosterRoundPoints = track.scores[rosterId];
          rosterRecord.roundsWon += rosterRoundPoints ?? 0;
          rosterRecord.roundsLost += scoreTotal - (rosterRoundPoints ?? 0);
        }
      }
    }
  }

  const sortedRecords = Array.from(records.values()).sort(compareRecords);
  sortedRecords[0].position = 1;
  for (let i = 1; i < sortedRecords.length; i++) {
    if (compareRecords(sortedRecords[i - 1], sortedRecords[i]) === 0) {
      sortedRecords[i].position = sortedRecords[i - 1].position;
    } else {
      sortedRecords[i].position = i + 1;
    }
  }

  return sortedRecords;
};

const compareRecords = (a: SmsSeasonRecord, b: SmsSeasonRecord): number => {
  if (a.wins > b.wins) {
    return -1;
  }

  if (b.wins > a.wins) {
    return 1;
  }

  if (a.setsWon > b.setsWon) {
    return -1;
  }

  if (b.setsWon > a.setsWon) {
    return 1;
  }

  if (a.roundsWon > b.roundsWon) {
    return -1;
  }

  if (b.roundsWon > a.roundsWon) {
    return 1;
  }

  if (a.roundsLost < b.roundsLost) {
    return -1;
  }

  if (b.roundsLost < a.roundsLost) {
    return 1;
  }

  return 0;
};
